import {
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WebsocketService } from '../services/websocket.service';
import { ClientRealtimeEventType, RealtimeMessageType } from '../domain/realtime.event.type';
import { filter } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AudioService } from '../services/audio.service';
import { ChatStore, UserMessageInput } from '../store/chat.store';
import { ActivatedRoute, Router } from '@angular/router';
import { ConversationService } from '../services/conversation.service';
import { IConversationDetails } from '../domain/conversation';
import { IMessage, MessageRole } from '../domain/message';
import { StringUtils } from '../utils/string.utils';
import { IAudioState } from '../domain/audio-state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkdownComponent } from 'ngx-markdown';
import { MessageControl } from './message-control/message-control';
import { AudioStreamTaskFactory } from '../audio/streaming/audio-stream.task-factory';
import { AudioTaskStore } from '../store/audio-task.store';
import { ToastrService } from 'ngx-toastr';
import { SplitByLetterPipe } from '../pipes/split-by-letter';
import { IFilePriview } from '../domain/file-preview';
import { Location } from '@angular/common';
import { readBase64 } from '../utils/file.utils';
import { AuthImagePipe } from '../pipes/auth-image.pipe';
import { HomeContentStore } from '../store/home-content.store';
import { IPromptPreset } from '../services/home-content.service';

@Component({
    selector: 'app-voice',
    imports: [CommonModule, FormsModule, MarkdownComponent, MessageControl, SplitByLetterPipe, AuthImagePipe],
    templateUrl: './voice.html',
    styleUrls: ['./voice.scss', '../../styles/_common.scss'],
})
export class Voice implements OnInit, OnDestroy {
    protected isUploadMenuOpen = false;
    protected messageText: string = '';
    protected base64Audio: string = '';
    protected isRecording = signal(false);
    protected isResponseReceived = signal(true);
    protected chatStore = inject(ChatStore);
    protected audioService = inject(AudioService);
    protected playingMessageId = signal<string | null>(null);
    // protected streamingPlayingMessageId = signal<string | null>(null);
    protected isAudioPlaying = signal(false);
    protected serverStatusMessage = signal('');
    protected readonly selectedFiles = signal<IFilePriview[]>([]);
    protected readonly homeContentStore = inject(HomeContentStore);

    private readonly _wbService = inject(WebsocketService);
    private readonly _route = inject(ActivatedRoute);
    private readonly _conversationService = inject(ConversationService);
    // private readonly _attachmentService = inject(AttachmentService);
    // private readonly _realtimeImageService = inject(RealtimeImageService);
    private readonly _router = inject(Router);
    private readonly _location = inject(Location);
    private readonly _audioStreamTaskFactory = inject(AudioStreamTaskFactory);
    private readonly _audioTaskStore = inject(AudioTaskStore);
    private mediaRecorder: MediaRecorder | null = null;
    private _destroyRef = inject(DestroyRef);
    private readonly _ngZone = inject(NgZone);
    private readonly _toastr = inject(ToastrService);
    private _mediaStream!: MediaStream;

    @HostListener('window:click', ['$event'])
    onGlobalClick(event: MouseEvent) {
        const clickedElement = event.target as HTMLElement;
        if (clickedElement.id === 'upload-menu' || clickedElement.id === 'upload-dropdown-panel') {
            return;
        }
        this.isUploadMenuOpen = false;
    }

    @ViewChild('scrollframe', { static: false }) scrollFrame: ElementRef | undefined;

    constructor() {
        const id = this._route.snapshot.paramMap.get('id');
        const sharedText = this._route.snapshot.queryParams['text'];

        if (sharedText) {
            this.messageText = sharedText;
        }

        if (id) {
            if (this.chatStore.revokePendingConversationId(id)) {
                return;
            }
            this._conversationService.get(id).subscribe({
                next: (conversation: IConversationDetails) => {
                    this.chatStore.setMessagesFromHistory(
                        id,
                        (conversation?.messages ?? []).map((m) => ({
                            id: m.messageId,
                            userText: m.content,
                            isResponse: m.role === MessageRole.Assistant,
                            attachments: m.attachments,
                            incomplete: m.isGenerating
                        })),
                    );
                },
                error: (err: any) => {
                    console.log('Failed to load conversation: ', err);
                },
            });
        } else if(this.chatStore.conversationId()) {
            this.setUrlConversationId(this.chatStore.conversationId() + '');
        }
        this.audioService.getState().subscribe((state: IAudioState) => {
            this._ngZone.run(() => {
                this.isAudioPlaying.set(state.playing);

                if (state.ended) {
                    this.playingMessageId.set(null);
                }
            });
        });
    }

    public ngOnInit(): void {
        void this.homeContentStore.ensureLoaded();
        this._audioStreamTaskFactory.preInit();
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.ConnectionAccepted))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onConnectionAccepted(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.ConversationCreated))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onConversationCreated(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.ConversationUpdated))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onConversationUpdated(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.OutputAudioTranscriptDelta))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onOutputAudioTranscriptDelta(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.OutputAudioDelta))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onOutputAudioDelta(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.InputAudioTranscriptDone))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onInputAudioTranscriptDone(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.InputAudioTranscriptFailed))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onInputAudioTranscriptFailed(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.ResponseDone))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onResponseDone(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.ServerStatus))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onServerStatus(msg);
            });
        this._wbService.message$
            .pipe(filter((msg) => msg.type === RealtimeMessageType.Error))
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg) => {
                this.onError(msg);
            });
        this._wbService.message$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((msg: any) => {
                console.log('ws message: ' + msg.type, msg);
            });
    }

    public ngOnDestroy(): void {
        this.close();
    }

    private onConnectionAccepted(msg: any): void {
        if (msg?.payload?.connectionId) {
            this.chatStore.connectionId.set(msg.payload.connectionId);
        }
    }

    private onConversationCreated(msg: any): void {
        if (!msg?.payload) {
            return;
        }
        const conversationId = msg.payload.conversationId;
        this.chatStore.setPendingConversationId(conversationId);
        this.setUrlConversationId(msg.payload.conversationId);
    }

    private onConversationUpdated(msg: any): void {
        if (!msg?.payload) {
            return;
        }
        this.setUrlConversationId(msg.payload.conversationId);
    }

    private setUrlConversationId(conversationId: string): void {
        this.chatStore.conversationId.set(conversationId);
        const urlTree = this._router.createUrlTree(['/', 'home', 'voice', conversationId]);
        const newUrl = this._router.serializeUrl(urlTree);
        this._location.go(newUrl);
    }

    private onOutputAudioTranscriptDelta(msg: any): void {
        if (msg?.payload?.text) {
            this.chatStore.appendLastMessageText(msg.payload.text);
            this.scrollToBottom();
        }
    }

    private onOutputAudioDelta(msg: any): void {
        if (msg?.payload?.audioBase64) {
            const responseMessage = this.chatStore.appendLastMessageAudioBytes(
                msg.payload.audioBase64,
            );
            this.scrollToBottom();
            const newBytes = StringUtils.base64ToBytes(msg.payload.audioBase64);
            if (this._audioTaskStore.audioStreamDestination) {
                this._audioTaskStore.audioStreamDestination(newBytes);
                this._audioTaskStore.setCurrentMessage(responseMessage);
            }
        }
    }

    private onInputAudioTranscriptDone(msg: any): void {
        if (msg?.payload?.text) {
            const voicePrompt = msg?.payload?.text;

            this.chatStore.appendLastRequestText(voicePrompt);
        }
    }

    private onInputAudioTranscriptFailed(msg: any): void {
        let errorMessage = 'Failed to parse voice message';
        if (msg?.payload?.error) {
            errorMessage = msg?.payload?.error;
        } else if (msg?.payload?.errorMessage) {
            errorMessage = msg?.payload?.errorMessage;
        }
        this._toastr.error('Error', errorMessage);
    }

    private onResponseDone(msg: any): void {
        this.isResponseReceived.set(true);
        this.serverStatusMessage.set('');

        const payload = msg.payload;
        const responseSnapshot = payload?.responseSnapshot;

        if (responseSnapshot) {
            this.chatStore.setLastResponseMessagePayload(
                responseSnapshot.assistantMessageId,
                responseSnapshot.text,
                responseSnapshot.audioBase64);
        }

        if (this._audioTaskStore.audioStreamDestination !== null) {
            this._audioTaskStore.audioStreamDestination('complete');
        }

        const lastMessage = this.chatStore.getLastMessage();
        if (!lastMessage) {
            return;
        }
        
        setTimeout(() => {
            this.scrollToBottom();
        }, 500);
    }

    private onServerStatus(msg: any): void {
        if (msg?.payload) {
            this.serverStatusMessage.set(msg.payload.message);
        }
    }

    private onError(msg: any): void {
        this.isResponseReceived.set(true);
        let errorMessage = 'Failed to get response';
        if (msg?.payload?.error) {
            errorMessage = msg?.payload?.error;
        } else if (msg?.payload?.errorMessage) {
            errorMessage = msg?.payload?.errorMessage;
        }
        this._toastr.error('Error', errorMessage);
    }

    protected send(): void {
        if ((!this.messageText && this.selectedFiles().length === 0) || !this.isResponseReceived()) {
            return;
        }

        const composeMessages = (): Promise<{ messageToSend: unknown, messageToRender: UserMessageInput }> => {
            if (this.selectedFiles().length > 0) {
                return this.fetchUploadedFiles()
                    .then((files) => ({
                        messageToSend: {
                            type: ClientRealtimeEventType.ImageInput,
                            conversationId: this.chatStore.conversationId(),
                            text: this.messageText,
                            images: files.map((f) => ({
                                imageBase64: f.data,
                                contentType: f.file.type,
                                mimeType: f.file.type,
                            })),
                        },
                        messageToRender: {
                            userText: this.messageText,
                            attachments: files.map(f => ({
                                attachmentId: crypto.randomUUID(),
                                contentType: f.file.type,
                                displayUrl: URL.createObjectURL(f.file),
                                sizeInBytes: 0,
                                storagePath: null,
                            }))
                        },
                    }));
            }

            return Promise.resolve({
                messageToSend: {
                    type: ClientRealtimeEventType.TextInput,
                    text: this.messageText,
                    conversationId: this.chatStore.conversationId(),
                },
                messageToRender: {
                    userText: this.messageText
                }
            });
        }

        composeMessages().then(data => {
            this.chatStore.addUserMessage(data.messageToRender);
            this.prepareAudioAndSend(data.messageToSend);
            this.messageText = '';
            this.selectedFiles.set([]);
            this.isResponseReceived.set(false);
        });
    }

    protected record() {
        if (this.isRecording()) {
            this.stopRecording();
        } else {
            this.startRecording();
        }
    }

    private prepareAudioAndSend(message: any) {
        const [audioTask, audioStreamDestination] =
            this._audioStreamTaskFactory.createAudioStreamTask();

        this._audioTaskStore.setCurrentMessage(null);
        this._audioTaskStore.audioStreamDestination = audioStreamDestination;
        this._audioTaskStore.setCurrentTask(audioTask).then(
            () => {
                this._wbService.sendMessage(message);
            },
            error => {
                this._toastr.error('An error occurred while initializing audio.' + (error ? ' ' + error: ''));
            });
    }

    private async startRecording() {
        await this._audioTaskStore.stopCurrentTask();
        const attachmentFiles = await this.fetchUploadedFiles();

        const imagesToSend = attachmentFiles.map((f) => ({
            imageBase64: f.data,
            contentType: f.file.type,
            mimeType: f.file.type,
        }));

        const imagesToDisplay = attachmentFiles.map((f) => ({
            attachmentId: crypto.randomUUID(),
            contentType: f.file.type,
            displayUrl: URL.createObjectURL(f.file),
            sizeInBytes: 0,
            storagePath: null,
        }));

        try {
            this._mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 24000,
                    channelCount: 1,
                    sampleSize: 16,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                },
            });
            this.mediaRecorder = new MediaRecorder(this._mediaStream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 128000,
            });
            this.mediaRecorder.ondataavailable = (event) => {
                StringUtils.convertBlobToBase64(event.data).then((audioBase64) => {
                    const message = {
                        type: ClientRealtimeEventType.AudioInputRaw,
                        mimeType: 'audio/webm;codecs=opus',
                        audioBase64: audioBase64,
                        conversationId: this.chatStore.conversationId(),
                    };
                    console.log('AudioInputRaw', message);
                    this._wbService.sendMessage(message);
                    if (!this.isRecording()) {
                        const commitmessage = {
                            type: ClientRealtimeEventType.AudioInputCommit,
                            conversationId: this.chatStore.conversationId(),
                            ...(imagesToSend.length > 0 ? { images: imagesToSend } : {})
                        };

                        console.log('AudioInputCommit', commitmessage);

                        this.prepareAudioAndSend(commitmessage);
                        this.selectedFiles.set([]);
                    }
                });
            };

            this.mediaRecorder.onstop = () => {
                this.isResponseReceived.set(false);
                this.isRecording.set(false);
            };

            this.mediaRecorder.start();
            this.isRecording.set(true);
            this.chatStore.addUserMessage({ userText: '', attachments: imagesToDisplay });
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    }

    private stopRecording() {
        if (this.mediaRecorder && this.isRecording()) {
            this.mediaRecorder.stop();
        }
        if(this._mediaStream) {
            this._mediaStream.getTracks().forEach(track => track.stop());
        }
    }

    protected startNewConversation() {
        this.chatStore.startNewConversation();
        this._router.navigate(['/', 'home', 'voice']);
    }

    private convertFloat32ToInt16(buffer: Float32Array): Int16Array {
        const l = buffer.length;
        const buf = new Int16Array(l);
        for (let i = 0; i < l; i++) {
            const s = Math.max(-1, Math.min(1, buffer[i]));
            buf[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return buf;
    }

    private convertInt16ArrayToBase64(int16Array: Int16Array): string {
        const uint8Array = new Uint8Array(
            int16Array.buffer,
            int16Array.byteOffset,
            int16Array.byteLength,
        );
        const binaryString = uint8Array.reduce((acc, byte) => {
            return acc + String.fromCharCode(byte);
        }, '');
        return window.btoa(binaryString);
    }

    private scrollToBottom(): void {
        if (!this.scrollFrame) {
            return;
        }
        const scrollContainer = this.scrollFrame.nativeElement;
        scrollContainer.scroll({
            top: scrollContainer.scrollHeight,
            left: 0,
            behavior: 'smooth',
        });
    }

    protected onCameraPhoto($event: Event) {
        const files = ($event.target as HTMLInputElement).files;
        if (files === null || files.length === 0) {
            return;
        }
        const availableFilesCount = MAX_FILES_UPLOAD_COUNT - this.selectedFiles().length ;
        if (availableFilesCount < files.length) {
            this._toastr.warning(`You can uplod up to ${MAX_FILES_UPLOAD_COUNT} files only.`)
        }
        [...files].splice(0, availableFilesCount).forEach(file => {
            if (file) {
                const connectionId = this.chatStore.connectionId();
                if (!this.isResponseReceived()) {
                    this._toastr.warning('You cannot uplaod file until analyzing is in progress.');
                    return;
                }
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    const dataUrl = reader.result as string;
                    const newFile = {
                        file: file,
                        url: dataUrl
                    };
                    this.selectedFiles.update(files => [...files, newFile]);
                    // const base64String = dataUrl.split(',')[1];
                    // this.prepareAudioAndSend({
                    //     type: ClientRealtimeEventType.ImageInput,
                    //     imageBase64: base64String,
                    //     contentType: file.type,
                    //     mimeType: file.type,
                    //     conversationId: this.chatStore.conversationId(),
                    // });
                };
                // this.messageText = '';
                // this.chatStore.addMessage({
                //     id: crypto.randomUUID(),
                //     text: signal(''),
                //     audioBytes: signal(new Uint8Array()),
                //     isResponse: false,
                //     conversationId: this.chatStore.conversationId(),
                //     attachments: [
                //         {
                //             attachmentId: crypto.randomUUID(),
                //             contentType: file.type,
                //             displayUrl: URL.createObjectURL(file),
                //             sizeInBytes: 0,
                //             storagePath: null,
                //         },
                //     ],
                //     files: null,
                // });
            }
        });
        ($event.target as HTMLInputElement).value = '';
    }

    protected removeSelectedFile(indexToRemove: number): void {
        this.selectedFiles.update(files =>
            files.filter((_, index) => index != indexToRemove)
        );
    }

    protected getDisplayableAttachments(message: IMessage) {
        return (message.attachments ?? []).filter((x) => x.contentType?.startsWith('image'));
    }

    protected getPlayableAttachments(message: IMessage) {
        return (message.attachments ?? []).filter((x) => x.contentType?.startsWith('audio'));
    }

    protected applyPrompt(item: IPromptPreset) {
        const message = {
            type: ClientRealtimeEventType.TextInput,
            text: item.prompt,
            conversationId: this.chatStore.conversationId(),
        };

        this.chatStore.addUserMessage({ userText: item.prompt });
        this.prepareAudioAndSend(message);
        this.isResponseReceived.set(false);
    }

    protected close() {
        this._audioTaskStore.stopCurrentTask();
    }

    private fetchUploadedFiles() {
        const files = this.selectedFiles();

        if (files.length === 0) {
            return Promise.resolve([]);
        }

        return Promise.all(
            files.map((f) =>
                readBase64(f.file).then((data) => ({ file: f.file, data })),
            ),
        );
    }
}

export const MAX_FILES_UPLOAD_COUNT = 5;
